import { Button, Checkbox, message, Popconfirm, Table, Typography, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import { api } from "api";
import { ApiService } from "api/api.service";
import { T_Api_Success_Res } from "api/api.types";
import { IconDocx, IconPdf } from "assets/icons/onboarding";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { SingleQueryWrapper } from "components/QueryWrapper";
import OnboardingLayout from "layout/OnboardingLayout";
import { ReactElement, useState } from "react";
import { TbChevronLeft, TbChevronRight, TbExclamationCircleFilled, TbTrash } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { T_Populate } from "types/_utils.types";
import { APPLICATION_STATUS, ApplicationSchemaDto } from "types/application.types";
import { FileSchemaDto } from "types/file.types";
import { LawSuitSchemaDto, LawSuitTypes } from "types/lawsuit.types";
import { LoanSchemaDto } from "types/loan.types";
import { AvailableResources } from "types/permissionGroup.types";
import { MAX_FILE_UPLOAD_SIZE } from "utils/constants";
import { getErrorMessage, humanFileSize } from "utils/functions";
import { ONBOARDING_ROUTES } from "utils/routes";
import { storedTokens } from "utils/storedTokens";
import ApplicationRedirect from "../components/ApplicationRedirect";
import PageTitle from "components/PageTitle";
import { queryClient } from "utils/queryClient";
import DashboardLayout from "layout/DashboardLayout";

const extensionIcons: Record<string, ReactElement> = {
  docx: <IconDocx />,
  pdf: <IconPdf />,
};

export function UploadLawsuits() {
  const applicationId = storedTokens.selectedApplicationId.getToken();
  const applicationDetails = ApiService.onboarding.useGetApplicationById(applicationId);

  const allLawsuits = ApiService.onboarding.useGetArtifacts(applicationId, "lawsuits");
  const allLoans = ApiService.onboarding.useGetArtifacts(applicationId, "loans");

  return (
    <DashboardLayout>
      <OnboardingLayout>
        <PageTitle title="Upload Lawsuit Docs" pageTitle={[]} />
        <SingleQueryWrapper
          showLoaderOn="isFetching"
          query={applicationDetails}
          renderData={(application) => {
            return (
              <SingleQueryWrapper
                showLoaderOn="isFetching"
                query={allLawsuits}
                renderData={(lawsuits) => {
                  return (
                    <SingleQueryWrapper
                      showLoaderOn="isFetching"
                      query={allLoans}
                      renderData={(loans) => {
                        return (
                          <UploadLawsuitsInternal
                            lawsuits={lawsuits.result}
                            loans={loans.result}
                            application={application.result}
                          />
                        );
                      }}
                    />
                  );
                }}
              />
            );
          }}
        />
      </OnboardingLayout>
    </DashboardLayout>
  );
}
function UploadLawsuitsInternal({
  loans,
  lawsuits,
  application,
}: {
  lawsuits: T_Populate<
    LawSuitSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >[];
  loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
  application: ApplicationSchemaDto;
}) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLawsuits, setHasLawsuits] = useState<boolean>(lawsuits?.length > 0);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [lawsuitLoanMap, setLawsuitLoanMap] = useState<Record<string, string>>({});
  const [loanSummonMap, setLoanSummonMap] = useState<Record<string, boolean>>({});

  const updateManualLawsuit = ApiService.onboarding.useUpdateManualLawsuit();
  const updateStatus = ApiService.onboarding.useUpdateStatus(application._id);

  function hasSummon(loanId: string) {
    let hasSummonDoc: boolean = false;
    const existingLawsuit = lawsuits?.find((el) => el.loan === loanId);
    if (existingLawsuit) {
      hasSummonDoc = true;
    }
    if (loanId in loanSummonMap) {
      hasSummonDoc = loanSummonMap[loanId];
    }

    return hasSummonDoc;
  }

  function handleInsurance() {
    setIsLoading(true);
    updateManualLawsuit.mutate(
      { applicationId: application?._id, body: { lawsuit_insurance: true } },
      {
        onSuccess: () => {
          updateStatus.mutate(
            { status: APPLICATION_STATUS.LAWSUITS },
            {
              onSuccess: () => {
                setIsLoading(false);
                navigate(
                  application?.plaid_config && application?.plaid_config?.connected
                    ? ONBOARDING_ROUTES.ChoosePlan
                    : ONBOARDING_ROUTES.UploadBankDocs_1,
                );
              },
              onError: () => setIsLoading(false),
            },
          );
        },
        onError: () => setIsLoading(false),
      },
    );
  }

  const hasExisting = (lawsuits?.length > 0 && lawsuits?.map((el) => el.attachments).flat().length > 0) ?? false;
  return (
    <>
      <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5 mb-[98px]">
        <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto p-6">
          <div className="flex gap-4 justify-between items-center">
            <Typography.Title level={5}>Are you being sued?</Typography.Title>
            <div className="inline-flex gap-2">
              {lawsuits?.length > 0 ? (
                <ClearAllButton applicationId={application._id} />
              ) : (
                <Button
                  variant={hasLawsuits === false ? "solid" : "filled"}
                  color="primary"
                  size="large"
                  onClick={() => setHasLawsuits(false)}
                >
                  No
                </Button>
              )}
              <Button
                variant={hasLawsuits === true ? "solid" : "filled"}
                color="primary"
                size="large"
                onClick={() => setHasLawsuits(true)}
              >
                Yes
              </Button>
            </div>
          </div>
          {hasLawsuits ? (
            <Table
              className="mt-4"
              columns={[
                {
                  key: "creditor_name",
                  dataIndex: "creditor_name",
                  title: "Creditor",
                },
                {
                  key: "has_summon",
                  title: "Has Summon?",
                  render: (_, record) => {
                    return (
                      <Checkbox
                        checked={hasSummon(record._id)}
                        onChange={(e) => {
                          const val = e.target.checked;
                          const currUpload = Object.values(lawsuitLoanMap).includes(record._id);
                          const existingLawsuit = lawsuits?.find((el) => el.loan === record._id);
                          // const togglable = currUpload || !!existingLawsuit;

                          if (currUpload) {
                            return message.error(
                              "There are summons attached to this loan. Delete those first to uncheck",
                            );
                          }
                          if (existingLawsuit) {
                            return message.error(
                              `You can't uncheck existing lawsuits. Please "Clear Lawsuits" and start again`,
                            );
                          }
                          setLoanSummonMap((prev) => ({ ...prev, [record._id]: val }));
                        }}
                      />
                    );
                  },
                },
                {
                  key: "has_lawsuit",
                  title: "Lawsuit Files",
                  render: (_, record) => {
                    if (!hasSummon(record._id)) {
                      return null;
                    }
                    return (
                      <Upload
                        name="file"
                        accept=".pdf"
                        multiple={true}
                        beforeUpload={(file) => {
                          const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_UPLOAD_SIZE;
                          const isSameFile = fileList.find((el) => {
                            return el.name === file.name && el.size === file.size;
                          });
                          if (!isLtMaxSize) {
                            message.error(`File must smaller than ${MAX_FILE_UPLOAD_SIZE}MB!`);
                          } else if (isSameFile) {
                            message.error(
                              "File with same name and same size already exists. Please rename the file and re-upload",
                            );
                          } else {
                            setFileList((prev) => [...prev, file]);
                            setLawsuitLoanMap((prev) => ({ ...prev, [file.name]: record._id }));
                          }
                          return Upload.LIST_IGNORE;
                        }}
                      >
                        <Button>Upload</Button>
                      </Upload>
                    );
                  },
                },
              ]}
              dataSource={loans}
              expandable={{
                expandedRowRender: (record) => {
                  const currLoanLawsuitDocNames = Object.entries(lawsuitLoanMap)
                    .map(([fileName, loanId]) => {
                      if (loanId === record._id) {
                        return fileName;
                      } else return null;
                    })
                    .filter((el) => el && typeof el === "string");
                  const currLoanLawsuits = fileList.filter((el) => currLoanLawsuitDocNames.includes(el.name));

                  // existing lawsuit
                  const existingLawsuit = lawsuits?.find((el) => el.loan === record._id);

                  return (
                    <div className="flex flex-col gap-3">
                      {currLoanLawsuits.map((file) => {
                        const x = file.name?.split(".");
                        const extension = x[x.length - 1];
                        const fileName = file?.name;

                        return (
                          <div
                            key={file.uid}
                            className="bg-white flex items-center gap-2 p-2 border border-solid border-gray-300 rounded-lg"
                          >
                            <div className="text-3xl">{extensionIcons[extension]}</div>
                            <div className="mr-auto">
                              <Typography.Text className="!mb-1 block">{fileName}</Typography.Text>
                              <Typography.Text type="secondary" className="text-sm">
                                {humanFileSize(file?.size ?? 0)}
                              </Typography.Text>
                            </div>

                            <Button
                              variant="text"
                              color="danger"
                              danger
                              icon={<TbTrash />}
                              onClick={() => {
                                setFileList((prev) => {
                                  return prev.filter((el) => el.uid !== file.uid);
                                });
                                setLawsuitLoanMap((prev) => {
                                  const cloned = structuredClone(prev);
                                  const foundLawsuit = Object.entries(cloned).filter(([lawsuitDocName]) => {
                                    return lawsuitDocName !== file.name;
                                  });
                                  return Object.fromEntries(foundLawsuit);
                                });
                              }}
                            />
                          </div>
                        );
                      })}
                      {existingLawsuit && existingLawsuit?.attachments?.length > 0 ? (
                        <>
                          <Typography.Title level={5} className="!m-0">
                            Existing Lawsuits
                          </Typography.Title>
                          {existingLawsuit.attachments.map((file) => {
                            const x = file.original_file_name?.split(".");
                            const extension = x[x.length - 1];
                            const fileName = file?.original_file_name;
                            return (
                              <div
                                key={file._id}
                                className="bg-white flex items-center gap-2 p-2 border border-solid border-gray-300 rounded-lg"
                              >
                                <div className="text-3xl">{extensionIcons[extension]}</div>
                                <div className="mr-auto">
                                  <Typography.Text className="!mb-1 block">{fileName}</Typography.Text>
                                  <Typography.Text type="secondary" className="text-sm">
                                    {humanFileSize(file?.size ?? 0)}
                                  </Typography.Text>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : null}
                    </div>
                  );
                },
                rowExpandable: (record) => {
                  const currLoanLawsuitDocNames = Object.values(lawsuitLoanMap).filter((el) => el === record._id);
                  const existingLawsuit = lawsuits?.find((el) => el.loan === record._id);

                  return (
                    currLoanLawsuitDocNames?.length > 0 ||
                    (existingLawsuit ? existingLawsuit?.attachments?.length > 0 : false)
                  );
                },
                expandedRowKeys: [
                  ...Object.values(lawsuitLoanMap),
                  ...(lawsuits ? (lawsuits.map((el) => el.loan) as string[]) : []),
                ],
              }}
              rowKey={"_id"}
              pagination={false}
            />
          ) : (
            <div className="flex gap-4 justify-between items-center mt-6">
              <Typography.Title level={5}>You are being insured for future lawsuits</Typography.Title>
              <div className="inline-flex gap-2">
                <Button
                  variant="solid"
                  color="primary"
                  size="large"
                  onClick={() => {
                    handleInsurance();
                  }}
                >
                  Let&apos;s Proceed
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="fixed left-0 right-0 bottom-0 flex justify-between bg-white py-3 px-5 border-t border-solid border-[0px] border-gray-300 ">
        <Button
          size="large"
          icon={<TbChevronLeft />}
          onClick={() => {
            navigate(ONBOARDING_ROUTES.UploadLoanDocs_2);
          }}
        >
          Back
        </Button>
        <Button
          size="large"
          type="primary"
          icon={<TbChevronRight />}
          iconPosition="end"
          className="w-fit"
          loading={isLoading}
          onClick={async () => {
            if (hasLawsuits === false) {
              handleInsurance();
            } else {
              // console.log(Object.keys(lawsuitLoanMap).length, fileList.length);

              if (Object.keys(lawsuitLoanMap).length !== fileList.length) {
                return message.error("Please choose creditor name for each lawsuit");
              }
              try {
                setIsLoading(true);
                let resFile: {
                  attachments: string[];
                  files: FileSchemaDto[];
                } = { attachments: [], files: [] };

                if (fileList.length > 0) {
                  const formData = new FormData();
                  fileList.forEach((file) => {
                    formData.append("files", file);
                  });
                  formData.append("category", AvailableResources.LAWSUIT);

                  const config: AxiosRequestConfig = {
                    // handles the upload progress
                    onUploadProgress: (event) => {
                      const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
                      // setUploadPercent(percent);
                    },
                  };

                  setIsLoading(true);
                  // post request to the api
                  const res: AxiosResponse<T_Api_Success_Res<{ attachments: string[]; files: FileSchemaDto[] }>> =
                    await api.post("/file/upload", formData, config);

                  resFile = res.data.result;
                }
                type _T = Parameters<typeof updateManualLawsuit.mutate>[0]["body"]["lawsuits"];
                const toUploadLawsuits: _T = [];

                Object.entries(lawsuitLoanMap).forEach(([fileName, loanId]) => {
                  const existing = toUploadLawsuits.findIndex((el) => el.loan_id === loanId);
                  const attId = resFile.files.find((el) => el.original_file_name === fileName)?._id;
                  if (attId) {
                    if (existing >= 0) {
                      toUploadLawsuits[existing]?.attachments?.push(attId);
                    } else {
                      toUploadLawsuits.push({
                        loan_id: loanId,
                        loan_name: loans?.find((el) => el._id === loanId)?.creditor_name,
                        status: LawSuitTypes.CLOSED,
                        attachments: [attId],
                      });
                    }
                  }
                });
                Object.entries(loanSummonMap).forEach(([loanId, hasSummon]) => {
                  const existing = toUploadLawsuits.find((el) => el.loan_id === loanId);

                  if (hasSummon && !existing) {
                    toUploadLawsuits.push({
                      loan_id: loanId,
                      loan_name: loans?.find((el) => el._id === loanId)?.creditor_name,
                      status: LawSuitTypes.CLOSED,
                    });
                  }
                });
                if (toUploadLawsuits.length > 0) {
                  updateManualLawsuit.mutate(
                    {
                      applicationId: application._id,
                      body: { lawsuits: toUploadLawsuits },
                    },
                    {
                      onSuccess: () => {
                        updateStatus.mutate(
                          {
                            status: APPLICATION_STATUS.LAWSUITS,
                          },
                          {
                            onSuccess: () => {
                              setIsLoading(false);
                              navigate(
                                application?.plaid_config && application?.plaid_config?.connected
                                  ? ONBOARDING_ROUTES.ChoosePlan
                                  : ONBOARDING_ROUTES.UploadBankDocs_1,
                              );
                            },
                            onError: () => {
                              setIsLoading(false);
                            },
                          },
                        );
                      },
                      onError: () => {
                        setIsLoading(false);
                      },
                    },
                  );
                } else {
                  setIsLoading(false);
                  navigate(
                    application?.plaid_config && application?.plaid_config?.connected
                      ? ONBOARDING_ROUTES.ChoosePlan
                      : ONBOARDING_ROUTES.UploadBankDocs_1,
                  );
                }
              } catch (error) {
                setIsLoading(false);
                message.error(getErrorMessage(error));
              }
            }
            // else if (hasExisting && fileList.length === 0) {
            //   navigate(ONBOARDING_ROUTES.UploadBankDocs_1);
            // }
          }}
        >
          <span>Continue</span>
        </Button>
      </div>
    </>
  );
}

function ClearAllButton({ applicationId }: { applicationId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const resetLawsuits = ApiService.onboarding.useResetModule();
  return (
    <Popconfirm
      open={isOpen}
      title="Clear all lawsuits?"
      description="Once cleared, lawsuits can't be recovered"
      onConfirm={() => {
        resetLawsuits.mutate(
          {
            application_id: applicationId,
            body: {
              module: APPLICATION_STATUS.LAWSUITS,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["application/single", applicationId],
              });
              queryClient.invalidateQueries({
                queryKey: ["application", "artifact/all", "lawsuits", applicationId],
              });
            },
          },
        );
      }}
      icon={<TbExclamationCircleFilled className="fill-red-500" />}
      okButtonProps={{
        danger: true,
        icon: <TbTrash />,
        loading: resetLawsuits.isPending,
      }}
      onCancel={() => setIsOpen(false)}
      okText="Yes, Clear"
      cancelText="No"
    >
      <Button
        variant={"filled"}
        color="primary"
        size="large"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        Clear Lawsuits
      </Button>
    </Popconfirm>
  );
}
